import * as React from 'react';
import styled from 'styled-components';
import { pipe } from 'fp-ts/lib/function';
import * as O from 'fp-ts/lib/Option';

import { Entry, Layout } from './slots';
import { getLabelWidth, getLabelHeight, useFormStyleContext, FormStyleContext, LabelStyle } from './FormStyle';
import { LabelLocation } from './Label';

interface IGroupProps {
    isRequired: boolean,
    labelLocation: LabelLocation,
    className?: string,
}


const calcLabelWidth = 
    (ctx: FormStyleContext) =>
        pipe(
            getLabelWidth.getOption(ctx),
            O.fold(
                () => 140,
                (s) => s
            )
        );

const calcLabelHeight = 
    (ctx: FormStyleContext) =>
        pipe(
            getLabelHeight.getOption(ctx),
            O.fold(
                () => 40,
                (s) => s
            )
        );

export const Group: React.FunctionComponent<Partial<IGroupProps>> = ({ 
    children, 
    isRequired = false,
    labelLocation = 'left',
    className,
}) => {

    const [focus, updateFocus] = React.useState(false);

    const toggle = () => updateFocus(!focus);

    const ctx = useFormStyleContext();

    const labelWidth = calcLabelWidth(ctx);
    const labelHeight = calcLabelHeight(ctx);

    return (
        <Entry connectLayout={children}>
            <Wrapper
                className={className}
                labelWidth={labelWidth}
                labelHeight={labelHeight}
                labelLocation={labelLocation}>
                <Layout 
                    name="label"
                    isRequired={isRequired}
                    labelLocation={labelLocation}
                    labelHeight={labelHeight}
                    onClick={toggle}
                />
                <Layout 
                    name="control" 
                    focused={focus}
                />
                <Layout name="notice" />
            </Wrapper>
        </Entry>
    );
}

// -------------------------------------------------------------------------------------
//  Wrapper
// -------------------------------------------------------------------------------------

type PropsForWrapper = Pick<IGroupProps, 'labelLocation'> & LabelStyle;

const wrapperStyled = ({ labelLocation, labelWidth }: PropsForWrapper) => {
    switch(labelLocation){
        case 'left': {
            return {
                display: 'grid',
                gridTemplateColumns: `${labelWidth}px 1fr`,
                gridTemplateAreas: `
                    "label control"
                    ".     notice"
                `,
                alignItems: 'stretch',
                gridGap: '0 10px',
            }
        }
        case 'top': {
            return {
                display: 'block',
            }
        }
    }
}

const Wrapper = styled.div<PropsForWrapper>`
    ${wrapperStyled};

    margin-bottom: 15px;

    &:last-child {
        margin-bottom: 0;
    }
`;